import React, { useEffect, useState } from 'react';
import { Popup, ProviderConsumer } from '@fluentui/react-northstar';
import { CellPhoneIcon, ChatIcon, ChevronDownIcon, ContactInfoIcon, MailIcon, MapPinIcon, OrgIcon, PhoneIcon, ViewDashboardIcon } from '@fluentui/react-icons-mdl2';
import { MgtPersonCard, PersonCard } from '@microsoft/mgt-react';
import { useTranslation } from 'react-i18next';
import { UserAvatar } from '../UserAvatar';
import { ThemeColorScheme } from '../../common/TeamsTheme';
import { getCachedUser } from '../../cache/PlatformUserCache';
import { UserSource } from '../../utils/constants';
import { getStoredUserId, isStoredUser } from './storedUserId';

MgtPersonCard.config.sections.mailMessages = false;

// The API returns these with the user, but the ambient PlatformUser type does not declare them.
type UserDetails = PlatformUser & {
  Department?: string | null;
  CompanyName?: string | null;
  EmployeeId?: string | null;
  OfficePhone?: string | null;
  Office?: string | null;
  StreetAddress?: string | null;
  City?: string | null;
  State?: string | null;
  ZipCode?: string | null;
  Country?: string | null;
};

// Source arrives as the enum number from the people search and as its name from PlatformUser(id).
const isTikitSource = (user: PlatformUser) =>
  (user?.Source as unknown) === UserSource.Tikit || (user?.Source as unknown) === UserSource[UserSource.Tikit];

// External users are kept up to date in Tikit rather than in Entra, so the stored details are the ones to show.
const showsStoredDetails = (user: PlatformUser) =>
  isTikitSource(user) || !user.AadObjectId || (user.IsExternalUser && getStoredUserId(user) > 0);

const DetailRow = ({ icon, label, value, href }: { icon: React.ReactNode; label: string; value: string; href?: string }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0' }} title={label}>
    <span style={{ display: 'flex', color: 'var(--mgt-theme-default-foreground)', opacity: 0.7 }}>{icon}</span>
    {href
      ? <a href={href} target="_blank" rel="noreferrer" style={{ color: 'var(--mgt-theme-brand-foreground)', wordBreak: 'break-all' }}>{value}</a>
      : <span style={{ wordBreak: 'break-word' }}>{value}</span>}
  </div>
);

const LabelRow = ({ label, value }: { label: string; value: string }) => (
  <div style={{ padding: '4px 0' }}>
    <div style={{ fontSize: '0.75rem', opacity: 0.7 }}>{label}</div>
    <div style={{ wordBreak: 'break-word' }}>{value}</div>
  </div>
);

const linkButtonStyle: React.CSSProperties = {
  background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: '0.8rem', color: 'var(--mgt-theme-brand-foreground)'
};

const Section = ({ title, rows, limit, seeMore, onSeeMore }: { title: string; rows: React.ReactNode[]; limit?: number; seeMore: string; onSeeMore?: () => void }) => (
  <div style={{ padding: '10px 0', borderBottom: '1px solid var(--mgt-theme-border-color)' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
      <span style={{ fontWeight: 600 }}>{title}</span>
      {!!onSeeMore && limit > 0 && rows.length > limit && <button type="button" style={linkButtonStyle} onClick={onSeeMore}>{seeMore}</button>}
    </div>
    {limit ? rows.slice(0, limit) : rows}
  </div>
);

type CardTab = 'overview' | 'contact' | 'job';

// Laid out like the Microsoft person card, with only the details Tikit stores for the user.
const TikitUserCard = ({ user }: { user: UserDetails }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [tab, setTab] = useState<CardTab>('overview');

  const address = [user.StreetAddress, user.City, [user.State, user.ZipCode].filter(Boolean).join(' '), user.Country].filter(Boolean).join(', ');
  const contactRows = [
    user.Email && <DetailRow key="email" icon={<MailIcon />} label={t('users.user-form.email-address')} value={user.Email} href={`mailto:${user.Email}`} />,
    user.MobilePhone && <DetailRow key="mobile" icon={<CellPhoneIcon />} label={t('users.user-form.mobile-phone')} value={user.MobilePhone} href={`tel:${user.MobilePhone}`} />,
    user.OfficePhone && <DetailRow key="office-phone" icon={<PhoneIcon />} label={t('users.user-form.office-phone')} value={user.OfficePhone} href={`tel:${user.OfficePhone}`} />,
    address && <DetailRow key="address" icon={<MapPinIcon />} label={t('users.user-form.street-address')} value={address} />
  ].filter(Boolean);
  const jobRows = [
    user.JobTitle && <LabelRow key="job-title" label={t('users.user-form.job-title')} value={user.JobTitle} />,
    user.Department && <LabelRow key="department" label={t('users.user-form.department')} value={user.Department} />,
    user.CompanyName && <LabelRow key="company" label={t('users.user-form.company-name')} value={user.CompanyName} />,
    user.Office && <LabelRow key="office" label={t('users.user-form.office')} value={user.Office} />,
    user.EmployeeId && <LabelRow key="employee-id" label={t('users.user-form.employee-id')} value={user.EmployeeId} />
  ].filter(Boolean);

  // With nothing stored beyond the email there is nothing to expand into, so keep the card to name and email.
  if (jobRows.length === 0 && contactRows.length <= 1)
    return (
      <div style={{ width: 340, padding: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <UserAvatar idOrUpn={user.AadObjectId ?? user.UserName} avatarProps={{ name: user.FullName || user.UserName, size: 'largest' }} />
          <div style={{ fontSize: '1.25rem', fontWeight: 600, wordBreak: 'break-word', minWidth: 0 }}>{user.FullName || user.UserName}</div>
        </div>
        <div style={{ borderTop: '1px solid var(--mgt-theme-border-color)', margin: '14px 0 8px' }} />
        {contactRows.length > 0 && <div style={{ fontWeight: 600, marginBottom: 4 }}>{t('users.user-form.contact-info')}</div>}
        {contactRows}
        {user.IsExternalUser && <div style={{ fontSize: '0.8rem', opacity: 0.7, marginTop: 8 }}>{t('users.user-form.external-user')}</div>}
      </div>
    );

  const seeMore = t('requests.see-more');
  const tabs = [
    { key: 'overview' as CardTab, icon: <ViewDashboardIcon />, label: t('users.tabs.user-info') },
    contactRows.length > 0 && { key: 'contact' as CardTab, icon: <ContactInfoIcon />, label: t('users.user-form.contact-info') },
    jobRows.length > 0 && { key: 'job' as CardTab, icon: <OrgIcon />, label: t('users.user-form.job-info') }
  ].filter(Boolean);

  return (
    <div style={{ width: 340 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '20px 20px 12px' }}>
        <UserAvatar idOrUpn={user.AadObjectId ?? user.UserName} avatarProps={{ name: user.FullName || user.UserName, size: 'largest' }} />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: '1.25rem', fontWeight: 600, wordBreak: 'break-word' }}>{user.FullName || user.UserName}</div>
          {user.JobTitle && <div style={{ fontSize: '0.9rem' }}>{user.JobTitle}</div>}
          {(user.Department || user.CompanyName) && <div style={{ fontSize: '0.8rem', opacity: 0.7 }}>{user.Department || user.CompanyName}</div>}
          {user.IsExternalUser && <div style={{ fontSize: '0.75rem', opacity: 0.7 }}>{t('users.user-form.external-user')}</div>}
        </div>
      </div>

      {!expanded && (
        <>
          <div style={{ display: 'flex', gap: 20, padding: '0 20px 12px' }}>
            {user.Email && (
              <a href={`mailto:${user.Email}`} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.85rem', color: 'var(--mgt-theme-brand-foreground)' }}>
                <MailIcon />{t('ticket-details.edit-ticket-form.requester-card.send-email')}
              </a>
            )}
            {user.Email && !isTikitSource(user) && (
              <a href={`https://teams.microsoft.com/l/chat/0/0?users=${user.Email}`} target="_blank" rel="noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.85rem', color: 'var(--mgt-theme-brand-foreground)' }}>
                <ChatIcon />{t('ticket-details.edit-ticket-form.requester-card.message-in-team')}
              </a>
            )}
          </div>
          {(contactRows.length > 0 || jobRows.length > 0) && (
            <button
              type="button"
              aria-label={seeMore}
              title={seeMore}
              onClick={() => setExpanded(true)}
              style={{ ...linkButtonStyle, display: 'flex', justifyContent: 'center', width: '100%', padding: '8px 0', borderTop: '1px solid var(--mgt-theme-border-color)' }}
            >
              <ChevronDownIcon />
            </button>
          )}
        </>
      )}

      {expanded && (
        <>
          <div style={{ display: 'flex', gap: 4, padding: '0 20px', borderBottom: '1px solid var(--mgt-theme-border-color)' }}>
            {tabs.map(item => (
              <button
                key={item.key}
                type="button"
                title={item.label}
                aria-label={item.label}
                aria-pressed={tab === item.key}
                onClick={() => setTab(item.key)}
                style={{
                  ...linkButtonStyle,
                  fontSize: '1rem',
                  padding: '8px 14px',
                  color: tab === item.key ? 'var(--mgt-theme-brand-foreground)' : 'var(--mgt-theme-default-foreground)',
                  borderBottom: `2px solid ${tab === item.key ? 'var(--mgt-theme-brand-foreground)' : 'transparent'}`
                }}
              >
                {item.icon}
              </button>
            ))}
          </div>
          <div style={{ maxHeight: 300, overflowY: 'auto', padding: '0 20px 8px' }}>
            {tab === 'overview' && (
              <>
                {contactRows.length > 0 && <Section title={t('users.user-form.contact-info')} rows={contactRows} limit={3} seeMore={seeMore} onSeeMore={() => setTab('contact')} />}
                {jobRows.length > 0 && <Section title={t('users.user-form.job-info')} rows={jobRows} limit={2} seeMore={seeMore} onSeeMore={() => setTab('job')} />}
              </>
            )}
            {tab === 'contact' && <Section title={t('users.user-form.contact-info')} rows={contactRows} seeMore={seeMore} />}
            {tab === 'job' && <Section title={t('users.user-form.job-info')} rows={jobRows} seeMore={seeMore} />}
          </div>
        </>
      )}
    </div>
  );
};

const CardContent = ({ user }: { user: PlatformUser }) => {
  const [fetched, setFetched] = useState<UserDetails>(null);

  const storedUserId = getStoredUserId(user);

  // The picker's value can come from the people search, which returns only a handful of fields, and the
  // user cache is not refreshed when a user is edited, so read the stored user afresh each time the card opens.
  useEffect(() => {
    let active = true;
    setFetched(null);
    if (showsStoredDetails(user) && storedUserId > 0)
      getCachedUser(storedUserId, true).then(full => active && full && setFetched(full)).catch(() => {});
    return () => { active = false; };
  }, [storedUserId]);

  const details = isStoredUser(fetched, storedUserId) ? fetched : user;

  if (showsStoredDetails(user))
    return <TikitUserCard user={details} />;

  return <PersonCard userId={user.AadObjectId} />;
};

export const UserDetailsCard = ({ user, trigger }: { user: PlatformUser; trigger: JSX.Element }) => {
  const [open, setOpen] = useState(false);

  return (
    <ProviderConsumer render={globalTheme => (
      <Popup
        open={open}
        onOpenChange={(_, data) => setOpen(!!data?.open)}
        position="below"
        align="start"
        trigger={trigger}
        content={{
          styles: { padding: 0, ...ThemeColorScheme(globalTheme.siteVariables) },
          // React bubbles portal events to the picker, whose control opens its menu on mousedown.
          content: open ? <div onMouseDown={(event) => event.stopPropagation()}><CardContent user={user} /></div> : null
        }}
      />
    )} />
  );
};
